import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { ContentHeading1, ContentHeading2, ContentText, ContentWrapper } from '@/components/ui/ContentText';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    try {
      await login(formData);
      // After successful login, redirect back to the originally requested page (if any)
      const params = new URLSearchParams(location.search);
      const fromRaw = params.get('from');
      const target = fromRaw ? decodeURIComponent(fromRaw) : '/';
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao fazer login');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  

  return (
    <ContentWrapper className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <ContentHeading1 className="text-3xl font-bold text-gray-900">
            IDE Missionários
          </ContentHeading1>
          <ContentText className="mt-2 text-sm text-gray-600">
            Sistema de Gerenciamento de Atividades Missionárias
          </ContentText>
        </div>

        {/* Login Form */}
        <Card className="bg-white shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <ContentHeading2 className="text-xl font-semibold text-gray-900 mb-6">
                Fazer Login
              </ContentHeading2>

              {error && (
                <div
                  className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md"
                  role="alert"
                  aria-live="assertive"
                  data-testid="login-error"
                >
                  <ContentText className="text-sm text-red-600">{error}</ContentText>
                </div>
              )}

              <div className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  leftIcon={<Mail className="h-4 w-4" />}
                  required
                  disabled={loading}
                />

                <Input
                  label="Senha"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Sua senha"
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                  required
                  disabled={loading}
                />
              </div>

              <div className="mt-2 flex justify-end">
                <Link to="/recuperar-senha" className="text-sm text-blue-600 hover:underline">
                  Recuperar senha
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </Card>

        {/* Footer */}
        <div className="text-center">
          <ContentText className="text-xs text-gray-500">
            © 2025 IDE Missionários. Todos os direitos reservados.
          </ContentText>
        </div>
      </div>
    </ContentWrapper>
  );
};

export default LoginPage;
